import React, { useEffect, useState } from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

import { useToast } from '../../contexts/ToastContext';
import SponsorManager from '../../components/admin/SponsorManager';
import FaviconManager from '../../components/admin/FaviconManager';
import HomePageContentEditor from '../../components/admin/HomePageContentEditor';
import CommunitySettingsEditor from '../../components/admin/CommunitySettingsEditor';
import { HomePageContent, mergeHomePageContent } from '../../constants/homePageDefaults';
import { CommunitySettings, mergeCommunitySettings } from '../../constants/communitySettingsDefaults';
import axios from 'axios';

interface SiteContent {
  about?: string;
  contact?: string;
  footer?: string;
  logoUrl?: string;
  faviconUrl?: string;
  communitySettings?: CommunitySettings;
  homePage?: HomePageContent;
}

const AdminContentManager: React.FC = () => {
  const [content, setContent] = useState<SiteContent>({});
  const [communitySettings, setCommunitySettings] = useState<CommunitySettings>(() => mergeCommunitySettings(null));
  const [homePage, setHomePage] = useState<HomePageContent>(() => mergeHomePageContent(null));
  const [loading, setLoading] = useState(true);
  const [savingSite, setSavingSite] = useState(false);
  const [savingCommunity, setSavingCommunity] = useState(false);
  const [savingHome, setSavingHome] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [signedLogoPreview, setSignedLogoPreview] = useState<string | null>(null);
  const { success, error } = useToast();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch('/api/admin/content', { credentials: 'include' });
        const data = await response.json();
        if (data.success) {
          const { homePage: storedHomePage, heroTitle, heroSubtitle, communitySettings: storedCommunity, ...siteFields } = data.data;
          setContent(siteFields);
          setCommunitySettings(mergeCommunitySettings(storedCommunity));
          setHomePage(mergeHomePageContent(storedHomePage, { heroTitle, heroSubtitle }));
          setLogoPreview(data.data.logoUrl || null);

          if (data.data.logoUrl) {
            try {
              const signedResponse = await fetch(
                `/api/admin/static-content/asset-url?key=${encodeURIComponent(data.data.logoUrl)}`
              );
              const signedData = await signedResponse.json();
              if (signedData.success) {
                setSignedLogoPreview(signedData.signedUrl);
              }
            } catch {
              // preview optional
            }
          }
        } else {
          error('Failed to fetch content: ' + data.message);
        }
      } catch (err) {
        console.error('AdminContentManager: Error fetching content:', err);
        error('Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContent((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setSignedLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    try {
      const formData = new FormData();
      formData.append('image', logoFile);

      const response = await axios.post('/api/upload/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      if (response.data.success) {
        const key = response.data.data.key || response.data.data.uploadDetails?.key;
        const accessUrl = response.data.data.accessUrl || response.data.data.logoUrl;
        setContent((prev) => ({ ...prev, logoUrl: key }));
        setLogoPreview(key || null);
        if (accessUrl) {
          setSignedLogoPreview(accessUrl);
        }

        success('Logo uploaded successfully!');
        setLogoFile(null);

        const fileInput = document.querySelector('#logo-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }
    } catch (err) {
      console.error('AdminContentManager: Logo upload error:', err);
      error('Failed to upload logo');
    }
  };

  const handleSaveSiteSettings = async () => {
    setSavingSite(true);
    try {
      const siteSettingsPayload = {
        about: content.about,
        contact: content.contact,
        footer: content.footer,
        logoUrl: content.logoUrl,
        faviconUrl: content.faviconUrl,
      };

      const response = await fetch('/api/admin/content', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(siteSettingsPayload),
        credentials: 'include',
      });
      const result = await response.json();
      if (result.success) {
        success('Site settings saved successfully!');
        window.dispatchEvent(new CustomEvent('contentUpdated'));
      } else {
        error('Failed to save content: ' + result.message);
      }
    } catch (err) {
      console.error('AdminContentManager: Save error:', err);
      error('Failed to save content');
    } finally {
      setSavingSite(false);
    }
  };

  const handleSaveCommunitySettings = async () => {
    setSavingCommunity(true);
    try {
      const response = await fetch('/api/admin/content/community-settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ communitySettings }),
        credentials: 'include',
      });
      const result = await response.json();
      if (result.success) {
        success('Community settings saved successfully!');
        if (result.data?.communitySettings) {
          setCommunitySettings(mergeCommunitySettings(result.data.communitySettings));
        }
      } else {
        const detail = result.errors?.join(', ') || result.message;
        error('Failed to save community settings: ' + detail);
      }
    } catch (err) {
      console.error('AdminContentManager: Community settings save error:', err);
      error('Failed to save community settings');
    } finally {
      setSavingCommunity(false);
    }
  };

  const handleSaveHomePage = async () => {
    setSavingHome(true);
    try {
      const response = await fetch('/api/admin/content/home-page', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ homePage }),
        credentials: 'include',
      });
      const result = await response.json();
      if (result.success) {
        success('Home page content saved successfully!');
        if (result.data?.homePage) {
          setHomePage(mergeHomePageContent(result.data.homePage));
        }
        window.dispatchEvent(new CustomEvent('contentUpdated'));
      } else {
        const detail = result.errors?.join(', ') || result.message;
        error('Failed to save home page: ' + detail);
      }
    } catch (err) {
      console.error('AdminContentManager: Home page save error:', err);
      error('Failed to save home page content');
    } finally {
      setSavingHome(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 space-y-6">
      <h1 className="text-3xl font-bold text-secondary mb-6">Admin Content Manager</h1>

      <Tabs defaultValue="site-settings" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="site-settings">Site Settings</TabsTrigger>
          <TabsTrigger value="home-page">Home Page</TabsTrigger>
        </TabsList>

        <TabsContent value="site-settings">
          <Card className="p-8 space-y-6 shadow-lg border-0 bg-surface rounded-xl">
            <CommunitySettingsEditor
              settings={communitySettings}
              onChange={setCommunitySettings}
              onSave={handleSaveCommunitySettings}
              saving={savingCommunity}
            />

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">About Section</label>
              <Textarea name="about" value={content.about || ''} onChange={handleInputChange} className="border-gray-300 focus:border-blue-500 min-h-[100px]" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Information</label>
              <Textarea name="contact" value={content.contact || ''} onChange={handleInputChange} className="border-gray-300 focus:border-blue-500 min-h-[100px]" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Footer Content</label>
              <Textarea name="footer" value={content.footer || ''} onChange={handleInputChange} className="border-gray-300 focus:border-blue-500 min-h-[100px]" />
            </div>

            <div className="bg-background p-6 rounded-lg">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Website Logo</label>

              {signedLogoPreview && (
                <div className="mb-4 p-4 bg-surface rounded-lg border border-border">
                  <p className="text-sm text-gray-600 mb-2">Current Logo:</p>
                  <img
                    src={signedLogoPreview}
                    alt="Logo Preview"
                    className="h-16 w-auto object-contain bg-surface shadow-sm rounded border"
                    onError={() => setSignedLogoPreview(null)}
                  />
                </div>
              )}

              <div className="space-y-3">
                <Input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" id="logo-upload" />
                <label
                  htmlFor="logo-upload"
                  className="flex items-center justify-center px-6 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
                >
                  <div className="text-center">
                    <span className="text-sm font-medium text-gray-700">
                      {logoFile ? logoFile.name : 'Choose logo file'}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>
                  </div>
                </label>

                {logoFile && (
                  <Button
                    onClick={handleLogoUpload}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium py-3 px-6 rounded-lg"
                  >
                    Upload Logo
                  </Button>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-6 border-t border-border">
              <Button
                onClick={handleSaveSiteSettings}
                disabled={savingSite}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-8 rounded-lg disabled:opacity-50"
              >
                {savingSite ? 'Saving...' : 'Save Site Settings'}
              </Button>
            </div>

            <div className="border-t border-border pt-8 mt-8">
              <SponsorManager />
            </div>

            <div className="border-t border-border pt-8 mt-8">
              <FaviconManager />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="home-page">
          <HomePageContentEditor
            homePage={homePage}
            onChange={setHomePage}
            onSave={handleSaveHomePage}
            saving={savingHome}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminContentManager;
